import type { Id } from "../core/ids";
import { transposeMidi } from "../theory/notes";

export type SequencerEvent =
  | {
      kind: "note";
      trackId: Id;
      tick: number; // relative to loop start
      durationTicks: number;
      note: number;
      velocity: number; // 0–1
      /** Pitched events follow song transposition; drums do not. */
      pitched: boolean;
    }
  | { kind: "click"; tick: number; accent: boolean };

export interface ScheduledEvent {
  event: SequencerEvent;
  /** Absolute tick since transport start (includes loop cycles). */
  absTick: number;
}

/**
 * Events of a looping sequence that fall in the half-open window [fromTick, toTick).
 * An event exactly at a loop boundary belongs to the next cycle.
 */
export function eventsInWindow(events: readonly SequencerEvent[], loopTicks: number, fromTick: number, toTick: number): ScheduledEvent[] {
  if (!(loopTicks > 0) || toTick <= fromTick) return [];
  const out: ScheduledEvent[] = [];
  const firstCycle = Math.floor(fromTick / loopTicks);
  // No epsilon here: the `abs < toTick` check below enforces the half-open window.
  const lastCycle = Math.floor(toTick / loopTicks);
  for (let c = firstCycle; c <= lastCycle; c++) {
    const base = c * loopTicks;
    for (const ev of events) {
      if (ev.tick < 0 || ev.tick >= loopTicks) continue;
      const abs = base + ev.tick;
      if (abs >= fromTick && abs < toTick) out.push({ event: ev, absTick: abs });
    }
  }
  out.sort((a, b) => a.absTick - b.absTick);
  return out;
}

export interface TransposeOutcome<T> {
  event: T | null;
  dropped: boolean;
}

/** Apply song transposition to a pitched note event; drums and clicks pass through. */
export function transposeEvent(ev: SequencerEvent, semitones: number): TransposeOutcome<SequencerEvent> {
  if (ev.kind !== "note" || !ev.pitched || semitones === 0) return { event: ev, dropped: false };
  const r = transposeMidi(ev.note, semitones);
  return r.note === null ? { event: null, dropped: true } : { event: { ...ev, note: r.note }, dropped: false };
}
